import {
  ApiError,
  type MailTestResponse,
  type MailTransport,
  type SMTPSecurity,
  type Settings as SettingsData,
  type SettingsRequest,
} from '@central/api';
import { Card, PageHeader, color } from '@central/ui';
import {
  Alert,
  Button,
  Group,
  NumberInput,
  PasswordInput,
  SegmentedControl,
  Switch,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { keys, useApi } from '../api';
import { CannedRepliesSettings } from '../components/CannedReplies';
import { QueryState } from '../components/parts';
import { errorMessage } from '../lib/errors';
import { useMe } from '../lib/me';

type Fields = Record<string, string>;

const transportOptions: Array<{ value: MailTransport; label: string }> = [
  { value: 'graph', label: 'Microsoft Graph' },
  { value: 'smtp', label: 'SMTP' },
  { value: 'none', label: 'Off' },
];

const securityOptions: Array<{ value: SMTPSecurity; label: string }> = [
  { value: 'starttls', label: 'STARTTLS' },
  { value: 'tls', label: 'TLS' },
  { value: 'none', label: 'None' },
];

/** Where the email the outbox sends now comes from, in words. */
function effectiveText(s: SettingsData): string {
  const e = s.mail.effective;
  if (e.transport === 'none')
    return e.origin === 'settings'
      ? 'Email is off: turned off here.'
      : 'Email is off: no transport is saved here and none is set in the environment.';
  const via = e.transport === 'graph' ? 'Microsoft Graph' : 'SMTP';
  return e.origin === 'env'
    ? `Sending through ${via} from the environment (MAIL_GRAPH_*). A transport saved here replaces it.`
    : `Sending through ${via}, saved here.`;
}

/**
 * Settings (Owner and Admin): how Central sends email, with a test send
 * through the saved transport, replies by email, and the inbox auto-close
 * period.
 */
export function Settings() {
  const api = useApi();
  const settings = useQuery({ queryKey: keys.settings, queryFn: () => api.settings.get() });
  return (
    <>
      <PageHeader
        title="Settings"
        subtitle="How Central sends email, and how long quiet conversations stay open."
      />
      <QueryState isPending={settings.isPending} error={settings.error}>
        {() => (
          <Stack gap={20} style={{ maxWidth: 760 }}>
            <EmailSettings settings={settings.data!} />
            <ReplySettings settings={settings.data!} />
            <InboxSettings settings={settings.data!} />
            <CannedRepliesSettings />
          </Stack>
        )}
      </QueryState>
    </>
  );
}

function useSave(onFields: (f: Fields) => void, onSaved?: () => void) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (b: SettingsRequest) => api.settings.update(b),
    onMutate: () => onFields({}),
    onSuccess: async (s) => {
      queryClient.setQueryData(keys.settings, s);
      onSaved?.();
      // The shell's email-off notice reads emailEnabled from me.
      await queryClient.invalidateQueries({ queryKey: keys.me });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.fields) onFields(e.fields);
    },
  });
}

function EmailSettings({ settings }: { settings: SettingsData }) {
  const m = settings.mail;
  const [transport, setTransport] = useState<MailTransport | undefined>(m.transport);
  const [tenantId, setTenantId] = useState(m.graph.tenantId);
  const [clientId, setClientId] = useState(m.graph.clientId);
  const [graphFrom, setGraphFrom] = useState(m.graph.from);
  const [host, setHost] = useState(m.smtp.host);
  const [port, setPort] = useState<number | ''>(m.smtp.port || '');
  const [security, setSecurity] = useState<SMTPSecurity>(m.smtp.security ?? 'starttls');
  const [username, setUsername] = useState(m.smtp.username);
  const [smtpFrom, setSmtpFrom] = useState(m.smtp.from);
  const [fromName, setFromName] = useState(m.smtp.fromName);
  // Secrets start blank: blank keeps what is stored.
  const [clientSecret, setClientSecret] = useState('');
  const [password, setPassword] = useState('');
  const [fields, setFields] = useState<Fields>({});
  const save = useSave(setFields, () => {
    setClientSecret('');
    setPassword('');
  });

  const graphBody = { tenantId, clientId, from: graphFrom, ...(clientSecret && { clientSecret }) };
  const smtpBody = {
    host,
    port: port === '' ? 0 : port,
    security,
    username,
    from: smtpFrom,
    fromName,
    ...(password && { password }),
  };
  const dirty =
    transport !== m.transport ||
    tenantId !== m.graph.tenantId ||
    clientId !== m.graph.clientId ||
    graphFrom !== m.graph.from ||
    host !== m.smtp.host ||
    (port || 0) !== m.smtp.port ||
    (transport === 'smtp' && security !== m.smtp.security) ||
    username !== m.smtp.username ||
    smtpFrom !== m.smtp.from ||
    fromName !== m.smtp.fromName ||
    clientSecret !== '' ||
    password !== '';

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const mail: NonNullable<SettingsRequest['mail']> = { ...(transport && { transport }) };
    if (transport === 'graph') mail.graph = graphBody;
    if (transport === 'smtp') mail.smtp = smtpBody;
    save.mutate({ mail });
  };
  const err = (k: string) => fields[`mail.${k}`];
  const unplaced =
    save.error &&
    !Object.keys(fields).some((k) => k.startsWith(`mail.${transport}.`) || k === 'mail.transport');

  return (
    <Card title="Email">
      <form
        onSubmit={submit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <Text size="sm" c={color.textMuted}>
          {effectiveText(settings)}
        </Text>
        <div>
          <Text size="sm" fw={500} mb={6} id="transport-label">
            Send email with
          </Text>
          <SegmentedControl
            aria-labelledby="transport-label"
            value={transport ?? ''}
            onChange={(v) => setTransport(v as MailTransport)}
            data={transportOptions}
          />
        </div>
        {transport === 'graph' && (
          <>
            <Text size="sm" c={color.textMuted}>
              An Entra ID app with the Mail.Send application permission (and Mail.Read for replies
              by email), restricted to the mailbox email is sent from.
            </Text>
            <TextInput
              label="Directory (tenant) ID"
              value={tenantId}
              error={err('graph.tenantId')}
              onChange={(e) => setTenantId(e.currentTarget.value)}
            />
            <TextInput
              label="Application (client) ID"
              value={clientId}
              error={err('graph.clientId')}
              onChange={(e) => setClientId(e.currentTarget.value)}
            />
            <PasswordInput
              label="Client secret"
              autoComplete="off"
              description={
                m.graph.clientSecretSet
                  ? 'Set. Leave blank to keep it. It is never shown.'
                  : undefined
              }
              value={clientSecret}
              error={err('graph.clientSecret')}
              onChange={(e) => setClientSecret(e.currentTarget.value)}
            />
            <TextInput
              label="Send as"
              description="The shared mailbox every email comes from."
              placeholder="central@example.com"
              value={graphFrom}
              error={err('graph.from')}
              onChange={(e) => setGraphFrom(e.currentTarget.value)}
            />
          </>
        )}
        {transport === 'smtp' && (
          <>
            <Group grow align="flex-start">
              <TextInput
                label="Host"
                placeholder="smtp.example.com"
                value={host}
                error={err('smtp.host')}
                onChange={(e) => setHost(e.currentTarget.value)}
              />
              <NumberInput
                label="Port"
                placeholder="587"
                min={1}
                max={65535}
                allowDecimal={false}
                hideControls
                value={port}
                error={err('smtp.port')}
                onChange={(v) => setPort(typeof v === 'number' ? v : '')}
                style={{ maxWidth: 140 }}
              />
            </Group>
            <div>
              <Text size="sm" fw={500} mb={6} id="security-label">
                Encryption
              </Text>
              <SegmentedControl
                aria-labelledby="security-label"
                value={security}
                onChange={(v) => setSecurity(v as SMTPSecurity)}
                data={securityOptions}
              />
              {err('smtp.security') && (
                <Text size="xs" c="red" mt={4}>
                  {err('smtp.security')}
                </Text>
              )}
            </div>
            <TextInput
              label="Username"
              description="Leave blank for a server that needs no sign-in."
              autoComplete="off"
              value={username}
              error={err('smtp.username')}
              onChange={(e) => setUsername(e.currentTarget.value)}
            />
            <PasswordInput
              label="Password"
              autoComplete="off"
              description={
                m.smtp.passwordSet ? 'Set. Leave blank to keep it. It is never shown.' : undefined
              }
              value={password}
              error={err('smtp.password')}
              onChange={(e) => setPassword(e.currentTarget.value)}
            />
            <Group grow align="flex-start">
              <TextInput
                label="From address"
                placeholder="central@example.com"
                value={smtpFrom}
                error={err('smtp.from')}
                onChange={(e) => setSmtpFrom(e.currentTarget.value)}
              />
              <TextInput
                label="From name"
                placeholder="GoATMS Central"
                value={fromName}
                error={err('smtp.fromName')}
                onChange={(e) => setFromName(e.currentTarget.value)}
              />
            </Group>
          </>
        )}
        {transport === 'none' && (
          <Text size="sm" c={color.textMuted}>
            Nothing is emailed, whatever the environment sets. Invite and setup links are shown on
            screen.
          </Text>
        )}
        {err('transport') && <Alert color="red">{err('transport')}</Alert>}
        {unplaced && <Alert color="red">{errorMessage(save.error)}</Alert>}
        {save.isSuccess && !dirty && <Alert color="green">Email settings saved.</Alert>}
        <Group>
          <Button type="submit" color="dark" disabled={!dirty} loading={save.isPending}>
            Save email settings
          </Button>
        </Group>
      </form>
      <TestSend dirty={dirty} />
    </Card>
  );
}

/** Sends a test email through the saved transport and shows the answer. */
function TestSend({ dirty }: { dirty: boolean }) {
  const api = useApi();
  const me = useMe();
  const [to, setTo] = useState(me.user.email);
  const [fields, setFields] = useState<Fields>({});
  const test = useMutation({
    mutationFn: () => api.settings.testMail({ to: to.trim() }),
    onMutate: () => setFields({}),
    onError: (e) => {
      if (e instanceof ApiError && e.fields) setFields(e.fields);
    },
  });
  return (
    <section
      aria-label="Send a test email"
      style={{ borderTop: `1px solid ${color.border}`, marginTop: 20, paddingTop: 16 }}
    >
      <Group align="flex-end" wrap="wrap">
        <TextInput
          label="Send a test email to"
          value={to}
          error={fields.to}
          onChange={(e) => setTo(e.currentTarget.value)}
          style={{ flex: '1 1 260px' }}
        />
        <Button
          variant="default"
          disabled={dirty}
          loading={test.isPending}
          onClick={() => test.mutate()}
        >
          Send test email
        </Button>
      </Group>
      {dirty && (
        <Text size="xs" c={color.textMuted} mt={6}>
          Save first: the test uses the saved settings.
        </Text>
      )}
      {test.data && <TestResult r={test.data} to={to.trim()} />}
      {test.error && !fields.to && (
        <Alert color="red" mt={12}>
          {errorMessage(test.error)}
        </Alert>
      )}
    </section>
  );
}

function TestResult({ r, to }: { r: MailTestResponse; to: string }) {
  if (r.sent)
    return (
      <Alert color="green" mt={12}>
        Sent to {to}. Check that inbox.
      </Alert>
    );
  return (
    <Alert color="red" mt={12} title="The test email was not sent">
      {r.error}
    </Alert>
  );
}

/** Whether replies to conversation emails are posted to the conversation. */
function replyStatus(s: SettingsData): string {
  const m = s.mail;
  if (m.effective.inboundReplies)
    return 'On: replies to conversation emails are read from the Graph mailbox every minute.';
  if (m.inboundReplies)
    return 'Saved as on, but replies are read only while email is sent through Microsoft Graph.';
  return 'Off: conversation emails ask people to answer in the app.';
}

function ReplySettings({ settings }: { settings: SettingsData }) {
  const [on, setOn] = useState(settings.mail.inboundReplies);
  const [fields, setFields] = useState<Fields>({});
  const save = useSave(setFields);
  const dirty = on !== settings.mail.inboundReplies;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate({ mail: { inboundReplies: on } });
  };
  return (
    <Card title="Replies by email">
      <form
        onSubmit={submit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <Text size="sm" c={color.textMuted}>
          {replyStatus(settings)}
        </Text>
        <Switch
          label="Post replies to conversation emails"
          description="Each email carries a reply address made for its recipient. A reply from that person's own address is posted to the conversation as them; quoted text and signatures are cut, attachments are not kept. Needs Microsoft Graph with Mail.Read on the shared mailbox."
          checked={on}
          onChange={(e) => setOn(e.currentTarget.checked)}
        />
        {save.error && (
          <Alert color="red">{fields['mail.inboundReplies'] ?? errorMessage(save.error)}</Alert>
        )}
        {save.isSuccess && !dirty && <Alert color="green">Reply settings saved.</Alert>}
        <Group>
          <Button type="submit" color="dark" disabled={!dirty} loading={save.isPending}>
            Save reply settings
          </Button>
        </Group>
      </form>
    </Card>
  );
}

function InboxSettings({ settings }: { settings: SettingsData }) {
  const [days, setDays] = useState<number | ''>(settings.inbox.autoCloseDays);
  const [fields, setFields] = useState<Fields>({});
  const save = useSave(setFields);
  const dirty = days !== settings.inbox.autoCloseDays;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (days === '') return setFields({ 'inbox.autoCloseDays': 'Enter 0 to 365.' });
    save.mutate({ inbox: { autoCloseDays: days } });
  };
  return (
    <Card title="Inbox">
      <form
        onSubmit={submit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <NumberInput
          label="Close quiet conversations after"
          description="Days a conversation waiting on the customer can go without a new message before it is closed. The customer is emailed 3 days before. 0 never closes one."
          rightSection={<Text size="sm">days</Text>}
          rightSectionWidth={52}
          min={0}
          max={365}
          allowDecimal={false}
          allowNegative={false}
          value={days}
          error={fields['inbox.autoCloseDays']}
          onChange={(v) => setDays(typeof v === 'number' ? v : '')}
          style={{ maxWidth: 260 }}
        />
        {save.error && !fields['inbox.autoCloseDays'] && (
          <Alert color="red">{errorMessage(save.error)}</Alert>
        )}
        <Group>
          <Button type="submit" color="dark" disabled={!dirty} loading={save.isPending}>
            Save inbox settings
          </Button>
        </Group>
      </form>
    </Card>
  );
}
